if (!customElements.get('partner-gallery')) {
  customElements.define('partner-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const track = this.querySelector('.third-party-firmware__slides');
      const controls = this.querySelector('.third-party-firmware__gallery-controls');
      if (!track) return;
      const previews = Array.from(track.querySelectorAll('[data-preview-src]'));
      previews.forEach((button, index) => button.addEventListener('click', () => this.openPreview(previews, index, button)));
      if (!controls) return;
      const slides = Array.from(track.children);
      const buttons = controls.querySelectorAll('[data-direction]');
      const dots = controls.querySelectorAll('[data-slide]');
      const counter = controls.querySelector('[data-counter]');
      const currentIndex = () => slides.reduce((nearest, slide, index) =>
        Math.abs(slide.getBoundingClientRect().left - track.getBoundingClientRect().left) <
        Math.abs(slides[nearest].getBoundingClientRect().left - track.getBoundingClientRect().left) ? index : nearest, 0);
      const update = () => {
        const index = currentIndex();
        counter.textContent = `${index + 1} / ${slides.length}`;
        dots.forEach((dot, dotIndex) => {
          if (dotIndex === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };
      const goTo = (target) => {
        const index = (target + slides.length) % slides.length;
        track.scrollBy({
          left: slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left,
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      };
      const move = (direction) => goTo(currentIndex() + direction);
      buttons.forEach(button => button.addEventListener('click', () => move(Number(button.dataset.direction))));
      dots.forEach(dot => dot.addEventListener('click', () => goTo(Number(dot.dataset.slide))));
      track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      track.addEventListener('scroll', update, { passive: true });
      controls.hidden = false;
      update();
    }

    disconnectedCallback() {
      this.previewDialog?.close();
    }

    openPreview(previews, index, opener) {
      this.previewDialog?.close();
      const dialog = document.createElement('dialog');
      dialog.className = 'partner-image-preview';
      dialog.setAttribute('aria-label', 'Enlarged project images');
      dialog.innerHTML = `
        <button class="partner-image-preview__close" type="button" aria-label="Close image preview" autofocus>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
        </button>
        <div class="partner-image-preview__stage"><img class="partner-image-preview__image" alt=""></div>
        <button class="partner-image-preview__arrow partner-image-preview__arrow--previous" type="button" data-preview-direction="-1" aria-label="Previous image">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m14 6-6 6 6 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button class="partner-image-preview__arrow partner-image-preview__arrow--next" type="button" data-preview-direction="1" aria-label="Next image">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m10 6 6 6-6 6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <span class="partner-image-preview__counter" aria-live="polite" aria-atomic="true"></span>`;
      const image = dialog.querySelector('img');
      image.draggable = false;
      const arrows = dialog.querySelectorAll('[data-preview-direction]');
      const render = () => {
        const original = previews[index].querySelector('img');
        image.src = previews[index].dataset.previewSrc;
        image.alt = original?.alt || '';
        dialog.querySelector('.partner-image-preview__counter').textContent = `${index + 1} / ${previews.length}`;
        arrows.forEach(arrow => { arrow.hidden = previews.length < 2; });
      };
      const move = direction => {
        index = (index + direction + previews.length) % previews.length;
        render();
      };
      arrows.forEach(arrow => arrow.addEventListener('click', () => move(Number(arrow.dataset.previewDirection))));
      let swipeStart = null;
      let suppressSwipeClick = false;
      dialog.addEventListener('pointerdown', event => {
        suppressSwipeClick = false;
        if (!event.isPrimary) {
          swipeStart = null;
          return;
        }
        if (event.pointerType === 'mouse' || previews.length < 2 ||
            !matchMedia('(max-width: 749px)').matches || event.target.closest('button')) return;
        swipeStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
      });
      dialog.addEventListener('pointerup', event => {
        if (!swipeStart || swipeStart.id !== event.pointerId) return;
        const dx = event.clientX - swipeStart.x;
        const dy = event.clientY - swipeStart.y;
        swipeStart = null;
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
        suppressSwipeClick = true;
        move(dx < 0 ? 1 : -1);
      });
      dialog.addEventListener('pointercancel', () => { swipeStart = null; });
      dialog.addEventListener('click', event => {
        if (!suppressSwipeClick) return;
        suppressSwipeClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, true);
      dialog.querySelector('.partner-image-preview__close').addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const previousOverflow = document.documentElement.style.overflow;
      dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = previousOverflow;
        dialog.remove();
        if (this.previewDialog === dialog) this.previewDialog = null;
        if (opener.isConnected) opener.focus({ preventScroll: true });
      }, { once: true });
      document.body.append(dialog);
      render();
      this.previewDialog = dialog;
      dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
  });
}
